/* cspell:ignore entrancy */
/**
 * Hash-based router for the grizzly-web SPA.
 *
 * Routes are registered via `route(pattern, handler)`. Patterns use :param
 * syntax for dynamic segments (e.g. `/run/:run_id`).
 *
 * Navigation is client-side via `location.hash`. The `dispatchRoute()`
 * function is called on every hash change.
 */

import type { RouteMatch } from './types/domain.js'
import { action } from './galaxy/components/action-delegation.js'
import { esc } from './components/shared.js'

interface RegisteredRoute {
  re: RegExp
  keys: string[]
  handler: (params: Record<string, string>) => void
}

const _routes: RegisteredRoute[] = []
const _teardownFns: (() => void)[] = []

/** Register a one-shot cleanup to run before the next route dispatch. Multiple registrations are all called. */
export function registerTeardown(fn: () => void): void {
  _teardownFns.push(fn)
}

/**
 * Register a route handler for a URL pattern.
 * @param pattern e.g. `/run/:run_id` or `/work/:item_id/timeline`
 * @param handler Called with a params dict when the route matches
 */
export function route(pattern: string, handler: (params: Record<string, string>) => void): void {
  const keys: string[] = []
  const reStr = pattern.replace(/:([^/]+)/g, (_: string, k: string) => {
    keys.push(k)
    return '([^/]+)'
  })
  // eslint-disable-next-line security/detect-non-literal-regexp -- pattern is hardcoded by app developer, not user-supplied
  _routes.push({ re: new RegExp(`^${reStr}$`), keys, handler })
}

/** Safely decode a URI component. Returns empty string on malformed input
 *  rather than propagating raw attacker-controlled content. */
function safeDecodeURIComponent(value: string): string {
  try {
    return decodeURIComponent(value)
  } catch {
    return ''
  }
}

/** Match the current hash against registered routes. */
export function matchRoute(hash: string): RouteMatch | null {
  for (const r of _routes) {
    const m = hash.match(r.re)
    if (m) {
      const params: Record<string, string> = {}
      r.keys.forEach((k, i) => {
        params[k] = safeDecodeURIComponent(m[i + 1] ?? '')
      })
      return { handler: r.handler, params }
    }
  }
  return null
}

/** Navigate to a path by setting the location hash. */
export function navigate(path: string): void {
  location.hash = path
}

/** Dispatch the appropriate handler for the current hash. */
export function dispatchRoute(): void {
  // Re-entrancy guard — if a handler synchronously triggers another
  // hash change (e.g. via navigate()), skip the recursive dispatch.
  if (_dispatching) return
  _dispatching = true
  try {
    // Snapshot+clear before draining to prevent infinite loop if a
    // teardown calls registerTeardown() while we're draining.
    const fns = _teardownFns.splice(0)
    for (const fn of fns) {
      try {
        fn()
      } catch (err) {
        // Continue draining remaining teardowns even if one throws
        console.error('Teardown failed:', err)
      }
    }
    const rawHash = location.hash.slice(1).split('?')[0]
    const hash = rawHash === undefined || rawHash === '' ? '/' : rawHash
    const match = matchRoute(hash)
    // Only reset scroll on actual route change, not same-route re-renders
    if (!_lastHash || _lastHash !== hash) {
      window.scrollTo(0, 0)
    }
    _lastHash = hash
    if (match) {
      try {
        match.handler(match.params)
      } catch (err) {
        console.error('Route handler threw:', err)
        renderError(err instanceof Error ? err.message : 'Route handler failed')
      }
    } else {
      renderNotFound()
    }
    updateNavActive()
    announceRouteChange()
    // Move focus to main content after route change (WCAG 2.4.3 / SL-UA02)
    const app = document.getElementById('app')
    if (app && document.activeElement !== app) {
      app.focus()
    }
  } finally {
    _dispatching = false
  }
}

/** Mark the active sidebar item based on the current hash. */
export function updateNavActive(): void {
  const hash = location.hash || '#/'
  // Galaxy sidebar uses id-based nav items
  document.querySelectorAll('.sb-item').forEach((el) => {
    el.classList.remove('active')
    el.removeAttribute('aria-current')
  })
  if (hash.startsWith('#/run/') || hash === '#/' || hash === '#') {
    document.getElementById('nav-fleet')?.classList.add('active')
    document.getElementById('nav-fleet')?.setAttribute('aria-current', 'page')
  } else if (hash.startsWith('#/work')) {
    document.getElementById('nav-work')?.classList.add('active')
    document.getElementById('nav-work')?.setAttribute('aria-current', 'page')
  } else if (hash.startsWith('#/kb')) {
    document.getElementById('nav-kb')?.classList.add('active')
    document.getElementById('nav-kb')?.setAttribute('aria-current', 'page')
  } else if (hash.startsWith('#/packs')) {
    document.getElementById('nav-packs')?.classList.add('active')
    document.getElementById('nav-packs')?.setAttribute('aria-current', 'page')
  } else if (hash.startsWith('#/logs')) {
    document.getElementById('nav-logs')?.classList.add('active')
    document.getElementById('nav-logs')?.setAttribute('aria-current', 'page')
  } else if (hash.startsWith('#/graph')) {
    document.getElementById('nav-graph')?.classList.add('active')
    document.getElementById('nav-graph')?.setAttribute('aria-current', 'page')
  } else if (hash.startsWith('#/analytics')) {
    document.getElementById('nav-analytics')?.classList.add('active')
    document.getElementById('nav-analytics')?.setAttribute('aria-current', 'page')
  }
}

/** Announce route changes to screen readers (WCAG 2.4.3). */
let _announceTimeout: ReturnType<typeof setTimeout> | undefined
function announceRouteChange(): void {
  const announcer = document.getElementById('page-title-announcer')
  if (!announcer) {
    return
  }
  // Cancel any pending announcement to avoid stale announcements on rapid nav
  if (_announceTimeout !== undefined) {
    clearTimeout(_announceTimeout)
  }
  announcer.textContent = ''
  _announceTimeout = setTimeout(() => {
    _announceTimeout = undefined
    announcer.textContent = document.title.replace(/^\(\d+\)\s*/, '')
  }, 50)
}

/** Render an error page when a route handler throws. */
function renderError(message: string): void {
  const app = document.getElementById('app')
  if (app) {
    document.title = 'Error — Grizzly'
    app.innerHTML = `<div class="not-found">
      <h1>Application Error</h1>
      <p>An unexpected error occurred while loading this page.</p>
      <p><code>${esc(message)}</code></p>
      <button class="btn" ${action('navigate', '/')}>Back to Fleet</button>
    </div>`
    app.focus()
  }
}
/** Render the 404 page. */
function renderNotFound(): void {
  const app = document.getElementById('app')
  if (app) {
    document.title = '404 — Page Not Found — Grizzly'
    app.innerHTML = `<div class="not-found">
      <h1>404 — Page Not Found</h1>
      <p>The page <code>${esc(location.hash)}</code> does not exist.</p>
      <button class="btn" ${action('navigate', '/')}>Back to Fleet</button>
    </div>`
    app.focus()
  }
}

/** Track last dispatched hash to avoid unnecessary scroll resets. */
let _lastHash: string | undefined
/** Re-entrancy guard — prevents concurrent dispatchRoute() calls. */
let _dispatching = false

/** Initialize the router — call once from app.ts */
export function initRouter(): void {
  window.addEventListener('hashchange', dispatchRoute)
  // Only listen for DOMContentLoaded if it hasn't fired yet.
  // Otherwise dispatch immediately to avoid double-render when the
  // bundle loads after the DOM is already interactive.
  if (document.readyState === 'loading') {
    window.addEventListener('DOMContentLoaded', dispatchRoute, { once: true })
  } else {
    dispatchRoute()
  }
}

/** Tear down the router — removes event listeners. Call before unmounting the SPA. */
export function destroyRouter(): void {
  if (_announceTimeout !== undefined) {
    clearTimeout(_announceTimeout)
    _announceTimeout = undefined
  }
  window.removeEventListener('hashchange', dispatchRoute)
  window.removeEventListener('DOMContentLoaded', dispatchRoute)
  _dispatching = false
}
